import React from 'react';
import { FormattedMessage } from 'react-intl';

import styled from 'styled-components';

import { GroupCSRLevels, PlAN_TYPES } from '@constants/individualPortal/customGrouping/customGrouping.constants';
import { IAddMembersGroup, IGroupItems } from '../../types/customGroupingTypes';
import GroupedMembersCard from './GroupedMembersCard';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import MemberCard, { IMemberCardConfig } from '../common/MemberCard';
import useCustomGrouping from '../../hooks/useCustomGrouping';
import { useAppDispatch, useAppSelector, useModal, useStateCode } from '@hooks/index';
import { formatCurrency } from '@utils/tools/formatCurrenyToInteger';

const StyledOrWrapper = styled.div`
    text-align: center;
    background-color: ${({ theme }) => theme.gray8};
    width: fit-content;
    padding: 10px;
    margin-bottom: 10px;
    border-radius: 50%;
    border: ${({ theme }) => `1px solid ${theme.gray4}`};
`;

const StyledBorderWrapper = styled.div`
    border: ${({ theme }) => `1px solid ${theme.gray8}`};
    border-radius: 7px;
    margin: 3px 6px;
    padding: 3px 6px;
    margin-bottom: 10px;
`;

interface IGroupedMembersWrapperProps {
    groups: IAddMembersGroup[];
    planType: PlAN_TYPES;
}

export default function GroupedMembersWrapper(props: IGroupedMembersWrapperProps) {
    const { groups, planType } = props;
    const dispatch = useAppDispatch();
    const { shopForUnenrolledGroup, getMaxAptc, getMaxStateSubsidy, getUniqueGroup, getSelectedCount, isStateSubsidy3EligibleGroup } = useCustomGrouping({ dispatch });
    const { openModal } = useModal();
    const customGroupingState = useAppSelector(state => state.individualPortal.customGrouping);
    const { caseNumber } = customGroupingState;
    const { isBoldHeader } = useStateCode();

    function getUnenrolledMembersName(group: IAddMembersGroup) {
        const { AddedInEnrolledGroup } = group;

        const members = AddedInEnrolledGroup.memberList.reduce((acc: string[], member) => {
            if (
                (planType === PlAN_TYPES.HEALTH && !member.healthEnrollmentId) ||
                (planType === PlAN_TYPES.DENTAL && !member.dentalEnrollmentId)
            ) {
                acc.push(`${member.firstName} ${member.lastName}`);
            }
            return acc;
        }, []);

        return members.join(', ');
    }

    function isMemberEnrolled(member) {
        if (planType === PlAN_TYPES.HEALTH) {
            return member.healthEnrollmentId;
        } else {
            return member.dentalEnrollmentId;
        }
    }

    function getUnenrolledMembers(group) {
        const secondaryMembers: IMemberCardConfig[] = [];
        group.memberList.forEach(member => {
            if (isMemberEnrolled(member)) return;
            secondaryMembers.push({
                data: member,
                config: {
                    isCheckBoxVisible: true,
                    isChecked: member.isChecked,
                    isCheckBoxDisabled: member?.enrollmentId,
                },
            });
        });

        return secondaryMembers;
    }

    function getPrimaryCTA(group) {
        return {
            label:
                planType === PlAN_TYPES.HEALTH
                    ? 'customGrouping.shopForHealthPlans'
                    : 'customGrouping.shopForDentalPlans',
            onClick: ({ secondaryMembers }) => {
                const selectedGroup = {
                    ...group,
                    memberList: secondaryMembers
                        .filter(member => member.config.isChecked)
                        .map(member => ({
                            ...member.data,
                        })),
                };
                shopForUnenrolledGroup(
                    {
                        group: selectedGroup,
                        enablePopupForEndOfPHE: false,
                        isChangePlan: false,
                        typeOfPlan: planType,
                        caseNumber,
                    },
                    { openModal },
                );
            },
            isDisabled: ({ secondaryMembersState }) => !getSelectedCount(secondaryMembersState),
        };
    }

    function getGroupedPlanDetails(group: IGroupItems) {
        const headers: {
            header: string;
            headerValues?: Record<string, any>;
        }[] = [];

        if (planType === PlAN_TYPES.HEALTH) {
            if (group.groupCsr !== GroupCSRLevels.CS1) {
                headers.push({
                    header: 'customGrouping.csrMsg',
                });
            }
        }

        if (group?.stateSubsidy2Eligible) {
            headers.push({
                header: 'customGrouping.stateSubsidy2Eligible',
            });
        }

        if (isStateSubsidy3EligibleGroup(group)) {
            headers.push({
                header: 'customGrouping.stateSubsidy3Eligible',
            });
        }

        if (group?.maxAptc >= 0) {
            const maxAPTC = getMaxAptc(group, planType);
            headers.push({
                header: 'customGrouping.totalAPTC',
                headerValues: { amount: formatCurrency(maxAPTC) },
            });
        }

        if (group?.maxStateSubsidy >= 0) {
            const maxStateSubsidy = getMaxStateSubsidy(group);
            headers.push({
                header: 'customGrouping.totalStateSubsidy',
                headerValues: { amount: formatCurrency(maxStateSubsidy) },
            });
        }

        return {
            headers,
            issuerName: group.issuerName,
            planName: group.planName,
            netPremium: group.netPremium ? 'customGrouping.netPremium' : '',
            perimoumAmount: formatCurrency(group.netPremium),
        };
    }

    const newGroups = getUniqueGroup(groups);

    return (
        <>
            {groups.map(group => (
                <StyledBorderWrapper 
                    data-testid={`add_members_group_card_${group.AddedInEnrolledGroup.id}`} 
                    key={`add_members_group_card_${group.AddedInEnrolledGroup.id}`}
                >
                    <p className={`${isBoldHeader ? 'bold-header' : ''}`}>
                        <FormattedMessage id="customGrouping.addMembers" />
                    </p>
                    <p>
                        <FormattedMessage
                            id="customGrouping.sameLevel"
                            values={{ members: getUnenrolledMembersName(group) }}
                        />
                    </p>
                    <GroupedMembersCard group={group} planType={planType} />
                </StyledBorderWrapper>
            ))}
            <div className="d-flex justify-content-center" data-testid="custom_grouping_member_or">
                <StyledOrWrapper>
                    <FormattedMessage id="customGrouping.or" />
                </StyledOrWrapper>
            </div>
            {newGroups.map(group => (
                <StyledBorderWrapper 
                    data-testid={`new_members_group_card_${group.AddedInEnrolledGroup.id}`}
                    key={`new_members_group_card_${group.AddedInEnrolledGroup.id}`}
                >
                    <p className={`${isBoldHeader ? 'bold-header' : ''} mb-2`}>
                        <FormattedMessage
                            id={
                                planType === PlAN_TYPES.HEALTH
                                    ? 'customGrouping.shopNewHealthPlan'
                                    : 'customGrouping.shopNewDentalPlan'
                            }
                        />
                    </p>
                    <ConditionalRendering isTrue={!!group?.enrollmentGroup?.memberList.length}>
                        <MemberCard
                            id={String(group.enrollmentGroup.id)}
                            secondaryMembers={getUnenrolledMembers(group.enrollmentGroup)}
                            primaryCTA={getPrimaryCTA(group.enrollmentGroup)}
                            planDetails={getGroupedPlanDetails(group.enrollmentGroup)}
                        />
                    </ConditionalRendering>
                </StyledBorderWrapper>
            ))}
        </>
    );
}
